
<template>
  <a-modal v-model:open="open" width="1000px"
           class="vehicle-select-modal"
           footer=""
           :closable="false"
           :mask-closable="true"
           @cancel="handleCancelClose">
    <template #title>
      <div class="flex aic jcsb">
        <div class="modal_title">{{ modalTitle }}</div>
        <div class="modal-header-actions">
          <div class="out" @click="handleCancelClose">
            <CloseOutlined />
          </div>
        </div>
      </div>
    </template>
    <div class="vehicle-modal-content">
      <!-- 搜索区域 -->
      <div class="query-item">
        <div class="search-filters">
          <div>
            测试牌照
            <a-input class="inp ml24" placeholder="请输入" v-model:value="query.testCarNum"></a-input>
          </div>
          <div class="ml30">
            车架号
            <a-input class="inp ml24" placeholder="请输入" v-model:value="query.frameNum"></a-input>
          </div>
          <a-button class="ml30" @click="search" type="primary" ghost>查询</a-button>
          <a-button class="ml24" @click="reset" type="primary" ghost>重置</a-button>
        </div>
      </div>

      <!-- 表格 -->
      <a-table
          :columns="vehicleColumns"
          :data-source="vehicleList"
          :pagination="pagination"
          row-key="id"
          :loading="loading"
          size="middle"
          :scroll="{ x: 800, y: 'auto' }"
          class="mt23"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'testCarNum'">
            {{ record.testCarNum }}
            <div style="color:red;font-size:12px" v-if="record.plateExpireWarn">{{ record.plateExpireWarn }}</div>
          </template>
          <template v-if="column.key === 'vehicleType'">
            {{ getSingleTypeValue(appStore.SysDictVOList, 'vehicleInfo' + 'category' + record.vehicleType) }}
          </template>
          <template v-if="column.key === 'vehicleColor'">
            {{ getSingleTypeValue(appStore.SysDictVOList, 'vehicleInfo' + 'fuselageColor' + record.vehicleColor) }}
          </template>
          <template v-if="column.key === 'bindMonitorDevice'">
            {{ record.bindMonitorDevice == '0' ? '未安装' : '已安装' }}
          </template>
          <template v-if="column.key === 'action'">
            <a-button type="primary" size="small" @click="handleRowSelect(record)">选择</a-button>
          </template>
        </template>
      </a-table>
    </div>
  </a-modal>
</template>

<script setup>
import { ref, reactive, onMounted, defineProps, defineEmits, defineExpose, watch } from "vue";
import { CloseOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import useUserStore from '@/store/modules/user';
import useAppStore from "@/store/modules/app";
import { getSingleTypeValue } from "@/utils/matchDic.js";
import { getVehiclePageList } from "@/api/vehicle/index.js";

const store = useUserStore();
const appStore = useAppStore();

const props = defineProps({
  modalTitle: {
    type: String,
    default: () => "选择测试车辆"
  },
  visible: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["selectedEvent", "cancelEvent", "update:visible"]);

const open = ref(false);
const loading = ref(false);
const vehicleList = ref([]);

watch(() => props.visible, (newVal) => {
  open.value = newVal;
  if (newVal) {
    pagination.current = 1;
    getVehicleList();
  }
});

const query = reactive({
  deptName: "",
  frameNum: "",
  testCarNum: "",
});

const pagination = reactive({
  total: 0,
  size: "middle",
  defaultPageSize: 10,
  current: 1,
  pageSize: 10,
  showSizeChanger: true,
  showQuickJumper: false,
  pageSizeOptions: ["10", "20", "30"],
  showTotal: (total) => `共有 ${total} 条数据`,
  onChange: pageChange,
  showSizeChange: pageSizeChange,
});

const vehicleColumns = [
  {
    title: "车辆识别代码",
    key: "vin",
    dataIndex: "vin",
    width: 180,
  },
  {
    title: "测试牌照",
    dataIndex: "licenceNo",
    key: "licenceNo",
    width: 140,
  },
  {
    title: "类型",
    key: "vehicleTypeName",
    dataIndex: "vehicleTypeName",
    width: 200,
  },
  {
    title: "生产企业",
    key: "manufacturer",
    dataIndex: "manufacturer",
    width: 120,
  },
  {
    title: "生产日期",
    key: "productionDate",
    dataIndex: "productionDate",
    width: 120,
  },
  {
    title: "车辆颜色",
    key: "color",
    dataIndex: "color",
    width: 100,
  },
  {
    title: "操作",
    key: "action",
    fixed: 'right',
    width: 100,
  }
];

function pageChange(page, pageSizeVal) {
  pagination.current = page;
  getVehicleList();
}

function pageSizeChange(current, size) {
  pagination.pageSize = size;
  getVehicleList();
}

const search = () => {
  pagination.current = 1;
  getVehicleList();
};

const reset = () => {
  query.deptName = "";
  query.frameNum = "";
  query.testCarNum = "";
  pagination.current = 1;
  getVehicleList();
};

const handleRowSelect = (record) => {
  open.value = false;
  emit('update:visible', false);
  emit('selectedEvent', record);
};

const hideModal = () => {
  open.value = false;
  emit('update:visible', false);
};

const getVehicleList = () => {
  loading.value = true;

  const params = {
    pageIndex: pagination.current,
    pageSize: pagination.pageSize,
    conditionMap: {
      ...query,
      deptIdStr: store.deptId
    }
  };

  getVehiclePageList(params).then((res) => {
    if (res.code === 200) {
      vehicleList.value = res.data.records || res.data;
      pagination.total = res.data.total || vehicleList.value.length;
    } else {
      message.error(res.msg || '获取车辆列表失败');
      vehicleList.value = [];
      pagination.total = 0;
    }
  }).finally(() => {
    loading.value = false;
  });
};

const handleCancelClose = () => {
  open.value = false;
  emit('update:visible', false);
  emit('cancelEvent');
};

defineExpose({
  hideModal,
  open
});
</script>

<style lang="less" scoped>
.modal_title {
  font-size: 18px;
  font-family: PingFang SC, PingFang SC;
  font-weight: 500;
  color: rgba(24, 24, 24, 1);
  line-height: 25px;
}

.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.out {
  cursor: pointer;
}

.vehicle-modal-content {
  padding: 10px 0;
}

.query-item {
  margin-bottom: 16px;
}

.search-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.search-filters > div {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.inp {
  width: 180px;
}

.mt23 {
  margin-top: 23px;
}

.ml24 {
  margin-left: 24px;
}

.ml30 {
  margin-left: 30px;
}

:deep(.vehicle-select-modal .ant-modal-wrap) {
  pointer-events: auto !important;
  position: fixed !important;
}

:deep(.vehicle-select-modal .ant-modal-mask) {
  pointer-events: auto !important;
}

:deep(.vehicle-select-modal) {
  pointer-events: none;
}

:deep(.vehicle-select-modal .ant-modal-content) {
  pointer-events: auto;
}
</style>